// src/gac/PageFlipPanel.js
// Extracted from GlobalActionCenter.js (GAC modularization, phase 1). No behaviour change.
import React, { useState } from 'react';
import axios from '../axiosInstance';
import '../GlobalActionCenterStyles.css';

const PageFlipPanel = ({
    mode,
    selectedCompany,
    stores,
    selectedStores,
    setSelectedStores,
    executing,
    setExecuting,
    results,
    setResults,
    setErrors,
}) => {
    const [actionConfig, setActionConfig] = useState({
        displayPage: 1,
        labelFilter: 'all', // 'all', 'online', 'assigned'
        excludeStores: []
    });

    // Store table sorting and filtering
    const [storeSort, setStoreSort] = useState({ column: 'store', direction: 'asc' });
    const [storeFilter, setStoreFilter] = useState('');

    // Live job progress. A big store (tens of thousands of labels) spends minutes
    // fetching and flipping before any store row lands in `results`, so without
    // this the panel looks frozen for the whole run.
    const [progress, setProgress] = useState(null);

    // Handle store selection
    const toggleStoreSelection = (storeCode) => {
        setSelectedStores(prev => {
            if (prev.includes(storeCode)) {
                return prev.filter(s => s !== storeCode);
            } else {
                return [...prev, storeCode];
            }
        });
    };

    // Sort and filter stores
    const handleStoreSort = (column) => {
        setStoreSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const filteredAndSortedStores = React.useMemo(() => {
        let result = [...stores];

        // Filter
        if (storeFilter) {
            const filter = storeFilter.toLowerCase();
            result = result.filter(s =>
                s.store?.toLowerCase().includes(filter) ||
                s.storeName?.toLowerCase().includes(filter)
            );
        }

        // Sort
        result.sort((a, b) => {
            let aVal, bVal;
            switch (storeSort.column) {
                case 'store':
                    aVal = a.store || '';
                    bVal = b.store || '';
                    break;
                case 'storeName':
                    aVal = a.storeName || '';
                    bVal = b.storeName || '';
                    break;
                case 'labelCount':
                    aVal = a.labelCount || 0;
                    bVal = b.labelCount || 0;
                    break;
                case 'gatewayCount':
                    aVal = a.gatewayCount || 0;
                    bVal = b.gatewayCount || 0;
                    break;
                case 'articleCount':
                    aVal = a.articleCount || 0;
                    bVal = b.articleCount || 0;
                    break;
                default:
                    aVal = a.store || '';
                    bVal = b.store || '';
            }

            if (typeof aVal === 'number') {
                return storeSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            return storeSort.direction === 'asc'
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        });

        return result;
    }, [stores, storeFilter, storeSort]);

    // Select all stores (uses filtered list)
    const selectAllStores = () => {
        const filteredStoreIds = filteredAndSortedStores.map(s => s.store);
        if (selectedStores.length === filteredStoreIds.length &&
            filteredStoreIds.every(id => selectedStores.includes(id))) {
            setSelectedStores([]);
        } else {
            setSelectedStores(filteredStoreIds);
        }
    };

    // Execute page flip action
    const executePageFlip = async () => {
        if (!selectedCompany) {
            alert('Please select a company first');
            setExecuting(false);
            return;
        }

        const payload = {
            companyCode: selectedCompany.companyCode,
            displayPage: parseInt(actionConfig.displayPage),
        };

        // Add store codes if in byStore mode
        if (mode === 'byStore') {
            if (selectedStores.length === 0) {
                alert('Please select at least one store');
                setExecuting(false);
                return;
            }
            payload.storeCodes = selectedStores;
        }

        // Add excluded stores if any
        if (actionConfig.excludeStores && actionConfig.excludeStores.length > 0) {
            payload.excludeStores = actionConfig.excludeStores;
        }

        console.log('Executing page flip with payload:', payload);

        // Start async job
        const startResponse = await axios.post('global-action/actions/page-flip-all-stores-async', payload);

        if (!startResponse.data.success) {
            setErrors(['Failed to start page flip: ' + (startResponse.data.message || 'Unknown error')]);
            return;
        }

        const jobId = startResponse.data.jobId;
        console.log('Page flip job started:', jobId);

        // Poll for status
        const pollInterval = setInterval(async () => {
            try {
                const statusResponse = await axios.get(`/global-action/actions/job-status/${jobId}`);

                if (statusResponse.data.success) {
                    const job = statusResponse.data.job;

                    // Update results with progress
                    setResults(job.results);
                    setProgress(job.progress || null);

                    // Check if complete
                    if (job.status === 'completed' || job.status === 'failed') {
                        clearInterval(pollInterval);
                        setExecuting(false);
                        setProgress(null);

                        if (job.status === 'failed') {
                            setErrors(['Job failed: ' + (job.error || 'Unknown error')]);
                        } else {
                            console.log('Page flip complete:', job.results);
                        }
                    }
                }
            } catch (error) {
                console.error('Error polling job status:', error);
                clearInterval(pollInterval);
                setExecuting(false);
                setProgress(null);
                setErrors(['Error checking job status: ' + error.message]);
            }
        }, 2000); // Poll every 2 seconds
    };

    // Same sequence the shell's executeAction dispatcher used for this action.
    // NOTE: `executing` returns to false as soon as the job is started (the poll keeps
    // running). That is the existing behaviour — keep it.
    const handleExecute = async () => {
        setExecuting(true);
        setResults(null);
        setErrors([]);
        setProgress(null);
        try {
            await executePageFlip();
        } catch (error) {
            console.error('Error executing action:', error);
            setErrors(['Execution error: ' + (error.response?.data?.message || error.message)]);
        } finally {
            setExecuting(false);
        }
    };

    return (
        <>
            {/* Store Selection (By Store Mode Only - pageFlip uses multi-store table) */}
            {mode === 'byStore' && stores.length > 0 && (
                <div className="store-selector-section store-selector-compact">
                    <div className="section-header">
                        <h3>Select Stores</h3>
                        <input
                            type="text"
                            placeholder="Search stores..."
                            value={storeFilter}
                            onChange={(e) => setStoreFilter(e.target.value)}
                            className="store-search-input"
                        />
                        {selectedStores.length > 0 && (
                            <span className="selection-badge">
                                {selectedStores.length} selected
                            </span>
                        )}
                    </div>

                    <div className="store-batch-table-wrapper store-table-compact">
                        <table className="store-batch-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={filteredAndSortedStores.length > 0 &&
                                                filteredAndSortedStores.every(s => selectedStores.includes(s.store))}
                                            onChange={selectAllStores}
                                        />
                                    </th>
                                    <th
                                        className="sortable-header"
                                        onClick={() => handleStoreSort('store')}
                                    >
                                        Store Code {storeSort.column === 'store' && (storeSort.direction === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        className="sortable-header"
                                        onClick={() => handleStoreSort('storeName')}
                                    >
                                        Store Name {storeSort.column === 'storeName' && (storeSort.direction === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        className="sortable-header"
                                        style={{ textAlign: 'center' }}
                                        onClick={() => handleStoreSort('labelCount')}
                                    >
                                        Labels {storeSort.column === 'labelCount' && (storeSort.direction === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        className="sortable-header"
                                        style={{ textAlign: 'center' }}
                                        onClick={() => handleStoreSort('gatewayCount')}
                                    >
                                        Gateways {storeSort.column === 'gatewayCount' && (storeSort.direction === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th
                                        className="sortable-header"
                                        style={{ textAlign: 'center' }}
                                        onClick={() => handleStoreSort('articleCount')}
                                    >
                                        Articles {storeSort.column === 'articleCount' && (storeSort.direction === 'asc' ? '▲' : '▼')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedStores.map((store) => (
                                    <tr
                                        key={store.store}
                                        className={selectedStores.includes(store.store) ? 'selected-row' : ''}
                                    >
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedStores.includes(store.store)}
                                                onChange={() => toggleStoreSelection(store.store)}
                                            />
                                        </td>
                                        <td>{store.store}</td>
                                        <td>{store.storeName || 'No Name'}</td>
                                        <td style={{ textAlign: 'center' }}>{store.labelCount || 0}</td>
                                        <td style={{ textAlign: 'center' }}>{store.gatewayCount || 0}</td>
                                        <td style={{ textAlign: 'center' }}>{store.articleCount || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Action Configuration - Only show when action is selected */}
            {stores.length > 0 && (
                <div className="action-config-section">
                    <h3>Page Flip Configuration</h3>
                    <div className="config-panel">
                        <div className="config-row">
                            <label>Display Page:</label>
                            <select
                                value={actionConfig.displayPage}
                                onChange={(e) => setActionConfig({...actionConfig, displayPage: e.target.value})}
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(page => (
                                    <option key={page} value={page}>Page {page}</option>
                                ))}
                            </select>
                        </div>

                        <div className="config-row">
                            <label>Label Filter:</label>
                            <select
                                value={actionConfig.labelFilter}
                                onChange={(e) => setActionConfig({...actionConfig, labelFilter: e.target.value})}
                            >
                                <option value="all">All Labels</option>
                                <option value="online" disabled>Only Online Labels (Coming Soon)</option>
                                <option value="assigned" disabled>Only Assigned Labels (Coming Soon)</option>
                            </select>
                        </div>

                        <div className="action-buttons">
                            <button
                                className="execute-button"
                                onClick={handleExecute}
                                disabled={executing || (mode === 'byStore' && selectedStores.length === 0)}
                            >
                                {executing ? 'Executing...' : 'Execute Page Flip'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {progress && (
                <div className="page-flip-progress">
                    <div className="page-flip-progress-head">
                        {progress.phase === 'flipping' ? 'Flipping' : 'Reading labels'}
                        {progress.currentStoreCode ? ` — store ${progress.currentStoreCode}` : ''}
                        {progress.totalStores > 1
                            ? ` (${progress.currentStore} of ${progress.totalStores} stores)`
                            : ''}
                    </div>
                    <div className="page-flip-progress-detail">
                        {progress.phase === 'flipping'
                            ? `${(progress.labelsFlipped || 0).toLocaleString()} of ${(progress.labelsFetched || 0).toLocaleString()} labels flipped`
                            + ((progress.labelsFailed || 0) > 0 ? ` — ${progress.labelsFailed.toLocaleString()} failed` : '')
                            : `${(progress.labelsFetched || 0).toLocaleString()} labels read`}
                    </div>
                    {progress.phase === 'flipping' && progress.labelsFetched > 0 && (
                        <div className="page-flip-progress-bar">
                            <div
                                className="page-flip-progress-fill"
                                style={{
                                    width: `${Math.min(100, Math.round((((progress.labelsFlipped || 0) + (progress.labelsFailed || 0)) / progress.labelsFetched) * 100))}%`
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default PageFlipPanel;

// Rendered by the shell AFTER the errors section, exactly where the results block used to sit.
export const PageFlipResults = ({ results }) => (
    <>
        {results && (
            <div className="results-section">
                <h3>Execution Results</h3>

                {/* Page Flip Results */}
                {results.summary && (
                    <>
                        <div className="results-summary">
                            <div className="summary-card">
                                <div className="summary-label">Stores Processed</div>
                                <div className="summary-value">{results.summary.total_stores_processed}</div>
                            </div>
                            <div className="summary-card">
                                <div className="summary-label">Labels Found</div>
                                <div className="summary-value">{results.summary.total_labels_found}</div>
                            </div>
                            <div className="summary-card success">
                                <div className="summary-label">Labels Flipped</div>
                                <div className="summary-value">{results.summary.total_labels_flipped}</div>
                            </div>
                            <div className="summary-card error">
                                <div className="summary-label">Failed</div>
                                <div className="summary-value">{results.summary.total_failed}</div>
                            </div>
                            <div className="summary-card highlight">
                                <div className="summary-label">Success Rate</div>
                                <div className="summary-value">{results.summary.success_rate}%</div>
                            </div>
                        </div>

                        {/* Per-Store Results */}
                        {results.stores && (
                            <div className="store-results">
                                <h4>Store Results ({results.stores.length} stores)</h4>
                                <div className="results-table">
                                    <div className="results-header">
                                        <div className="col-store">Store</div>
                                        <div className="col-labels">Labels Found</div>
                                        <div className="col-flipped">Labels Flipped</div>
                                        <div className="col-failed">Failed</div>
                                        <div className="col-status">Status</div>
                                    </div>
                                    {results.stores.map((store, index) => (
                                        <div key={index} className={`results-row ${store.status}`}>
                                            <div className="col-store">{store.store_code}</div>
                                            <div className="col-labels">{store.labels_found}</div>
                                            <div className="col-flipped">{store.labels_flipped}</div>
                                            <div className="col-failed">{store.failed}</div>
                                            <div className="col-status">
                                                {store.status === 'success' && 'Success'}
                                                {store.status === 'failed' && 'Failed'}
                                                {store.status === 'pending' && 'Pending'}
                                            </div>
                                            {store.errors && store.errors.length > 0 && (
                                                <div className="col-errors">
                                                    {store.errors.map((err, i) => (
                                                        <div key={i} className="error-detail">{err}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        )}
    </>
);
